import { useCallback, useRef, useState } from 'react'
import { useWebSocket } from './useWebSocket'
import { BaseURLS } from '../api/axios'
import { TopDrop } from '../types'
import { useSelector } from 'react-redux'
import { selectAppState } from '../store/selectors'
import { useBreakpointValue } from '@chakra-ui/react'
import { formatSkinName } from '../utils/formatSkinName'
import { useFormatSkinName } from './useFormatName'

interface WebSocketMessage {
  type: 'topSkins' | 'newSkins'
  skins: TopDrop[]
}

export const useLuckyDrawEvents = ({
  iswowOnHover,
  isliveOnHover,
  inView,
  liveTimelineRef,
  wowTimelineRef,
}: {
  iswowOnHover: boolean
  isliveOnHover: boolean
  inView: boolean
  liveTimelineRef: any
  wowTimelineRef: any
}) => {
  const [error, setError] = useState<Error | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  // 分别存储 top 和 new 类型的皮肤
  const [topSkinsValues, setTopSkinsValues] = useState<{
    skins: TopDrop[]
    newEventsCount: number
  }>({
    skins: [],
    newEventsCount: 0,
  })
  const [newSkinsValues, setNewSkinsValues] = useState<{
    skins: TopDrop[]
    newEventsCount: number
  }>({
    skins: [],
    newEventsCount: 0,
  })

  // 分别存储之前的 top 和 new 类型的皮肤，用于比较
  const prevTopSkinsRef = useRef<TopDrop[]>([])
  const prevNewSkinsRef = useRef<TopDrop[]>([])
  // 是否正在开箱
  const { isOpeningBox } = useSelector(selectAppState)

  const TOTAL_LIVE_ITEMS =
    useBreakpointValue({
      base: 8,
      sm: 11,
      md: 16,
      lg: 20,
    }) ?? 11

  // test
  // const testUrl = 'ws://localhost:8080' // BaseURLS.ws

  const { isConnected } = useWebSocket({
    url: BaseURLS.ws,
    enabled: true,
    onMessage: useCallback(
      (wsData: WebSocketMessage) => {
        setIsLoading(false)
        setError(null)
        if (isOpeningBox) return
        const { type, skins: newEventSkins } = wsData
        if (type === 'topSkins') {
          if (iswowOnHover) return
          if (!inView && topSkinsValues.skins.length >= 4) return
          const prevSkins = prevTopSkinsRef.current
          const added = newEventSkins.filter(
            (newEvent) =>
              !prevSkins.some(
                (prevEvent) =>
                  prevEvent.instanceId === newEvent.instanceId &&
                  prevEvent?.user?.uuid === newEvent?.user?.uuid,
              ),
          )
          const isAnimationComplete =
            !wowTimelineRef.current ||
            wowTimelineRef.current.progress() >= 0.99 ||
            wowTimelineRef.current.totalProgress() === 1
          if (!isAnimationComplete) return
          if (newEventSkins.length === 5 && topSkinsValues.skins.length >= 4) return
          // console.log('added>>', added.length)
          if (added.length > 0) {
            setTopSkinsValues((prev) => {
              const newSkins = [...added, ...prev.skins].slice(0, 4)
              prevTopSkinsRef.current = newSkins
              return {
                skins: newSkins,
                newEventsCount: added.length === 5 ? 0 : added.slice(0, 4).length,
              }
            })
          }
        } else if (type === 'newSkins') {
          if (isliveOnHover) return
          if (!inView && newSkinsValues.skins.length >= TOTAL_LIVE_ITEMS) return
          const prevSkins = prevNewSkinsRef.current
          const added = newEventSkins.filter(
            (newEvent) =>
              !prevSkins.some(
                (prevEvent) =>
                  prevEvent.instanceId === newEvent.instanceId &&
                  prevEvent?.user?.uuid === newEvent?.user?.uuid,
              ),
          )
          // console.log('newEventSkins>>', newEventSkins.length)
          // console.log('added>>', added.length)
          const isAnimationComplete =
            !liveTimelineRef.current ||
            liveTimelineRef.current.progress() >= 0.99 ||
            liveTimelineRef.current.totalProgress() === 1
          if (!isAnimationComplete) return
          if (newEventSkins.length === 20 && newSkinsValues.skins.length >= TOTAL_LIVE_ITEMS) return
          if (added.length > 0) {
            requestAnimationFrame(() => {
              setNewSkinsValues((prev) => {
                const newSkins = [...added, ...prev.skins].slice(0, TOTAL_LIVE_ITEMS + added.length)
                // 更新 prevRef
                prevNewSkinsRef.current = newSkins
                return {
                  skins: newSkins,
                  newEventsCount: added.length === 20 ? 0 : added.slice(0, TOTAL_LIVE_ITEMS).length,
                }
              })
            })
          }
        }
      },
      [
        isOpeningBox,
        iswowOnHover,
        inView,
        topSkinsValues.skins.length,
        wowTimelineRef,
        isliveOnHover,
        newSkinsValues.skins.length,
        TOTAL_LIVE_ITEMS,
        liveTimelineRef,
      ],
    ),
    onError: (error: Event) => {
      console.error('WebSocket error:', error)
      setError(new Error(error.type))
      setIsLoading(false)
    },
  })

  // const [isAddSkin, setIsAddSkin] = useState(false)
  // // // test
  // useEffect(() => {
  //   if (newSkins.length > 1) {
  //     if (isAddSkin) return
  //     setIsAddSkin(true)
  //     setInterval(() => {
  //       const num = Math.floor(Math.random() * 5)
  //       console.log('test>> add skin')
  //       setNewNewSkinsEvents(newSkins.slice(-num))
  //       setNewSkins([...newSkins.slice(-num), ...newSkins].slice(0, TOTAL_LIVE_ITEMS + num))
  //     }, 1000)
  //   }
  // }, [TOTAL_LIVE_ITEMS, newSkins, isAddSkin])

  return {
    topSkinsValues,
    newSkinsValues,
    error,
    isConnected,
    isLoading,
    TOTAL_LIVE_ITEMS,
  }
}
